import { ConnectedPosition } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { PopupRef, PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { HierarchyItem } from '../../models/hierarchyItem';
import { HierarchyViewModel } from '../../models/hierarchyViewModel';
import { PopupHierarchyData } from '../../models/popupHierarchyData';
import { HierarchySubordinateItemsComponent } from '../hierarchy-subordinate-items/hierarchy-subordinate-items.component';

@UntilDestroy()
@Component({
  selector: 'hierarchy',
  templateUrl: './hierarchy.component.html',
  styleUrls: ['./hierarchy.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HierarchyComponent implements OnDestroy {
  viewModel: HierarchyViewModel = {} as HierarchyViewModel;
  openSuperiorItems: boolean = false;
  indexArray: number[];
  @Input() public set currentItem(value: HierarchyItem) {
    this.viewModel.currentItem = value;
  }
  @Input() public set superiorItems(value: HierarchyItem[]) {
    if (value) {
      this.viewModel.directSuperiorItem = value[0];
      if (value.length >= 2) {
        this.viewModel.mostSuperiorItem = value[value.length - 1];
        if (value.length > 2) {
          this.viewModel.superiorItems = value.slice(1, -1).reverse();
        }
      }
    }
  }
  @Input() public set subordinateItems(value: HierarchyItem[]) {
    if (value) {
      if (value.length > 1) {
        this.viewModel.subordinateItems = value;
        this.indexArray = Array.from(Array(Math.min(3, value.length)).keys());
      } else {
        this.viewModel.firstSubordinateItem = value[0];
      }
    }
  }

  @Input() smallWidthPreview: boolean = true;
  @Input() previewPopupLarge: boolean;
  @Input() isPopUp: boolean = false;
  @Input() focusedElementId: string;
  @Input() selectedElement: string;
  private compPopupRef: PopupRef<HierarchySubordinateItemsComponent, PopupHierarchyData>;
  @Output() selectedItem: EventEmitter<HierarchyItem> = new EventEmitter<HierarchyItem>();
  @Output() onOpenSuperiorItems: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() openSubordinateItemsList: EventEmitter<void> = new EventEmitter<void>();
  @ViewChildren('hierarchyItem', { read: ElementRef }) superiorItemsRef: QueryList<ElementRef>;
  @ViewChildren('hierarchyHiddenItem', { read: ElementRef }) hiddenSuperiorsRef: QueryList<ElementRef>;
  @ViewChild('count', { read: ElementRef }) countRef: ElementRef;
  @ViewChild('hierarchyAboveItem', { read: ElementRef }) superiorAboveRef: ElementRef;
  @ViewChild('hierarchyBelowItem', { read: ElementRef }) superiorBelowRef: ElementRef;
  @ViewChildren('avatarBelowItem', { read: ElementRef }) avatarBelowItems: QueryList<ElementRef>;
  @ViewChild('openHierarchyIcon', { read: ElementRef }) openHierarchyRef: ElementRef;

  get isLargeMode(): boolean {
    return (!this.smallWidthPreview && !this.isPopUp) || this.previewPopupLarge;
  }

  constructor(private popupService: PopupService) {}

  ngOnDestroy(): void {
    this.compPopupRef?.close();
  }

  emitOpenSuperiorItems() {
    this.openSuperiorItems = true;
    this.onOpenSuperiorItems.emit(true);
  }

  openPopupSubordinateItems(event: MouseEvent): void {
    this.openSubordinateItemsList.emit();
    let { x, y } = event;
    if (!x && !y) {
      let rect = this.openHierarchyRef.nativeElement.getBoundingClientRect();
      x = rect.left + window.scrollX;
      y = rect.top + window.scrollY;
    }
    y += 10;
    const position: ConnectedPosition[] = [{ originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'top', offsetY: 8 }];
    this.compPopupRef = this.popupService.open<HierarchySubordinateItemsComponent, PopupHierarchyData>(
      { x, y },
      HierarchySubordinateItemsComponent,
      { items: this.viewModel.subordinateItems },
      { position }
    );
    this.compPopupRef.destroy$.pipe(untilDestroyed(this)).subscribe(() => {
      if (this.compPopupRef.data.selectedItem) {
        this.onSelectedItem(this.compPopupRef.data.selectedItem, this.compPopupRef.data.selectedItem?.index);
      }
    });
  }

  onSelectedItem(selectedItem: HierarchyItem, index: number, position?: string): void {
    if (!(position === 'subordinate' && this.viewModel.subordinateItems.length > 3)) {
      this.selectedItem.emit({ index: index, ...selectedItem });
    }
  }
}
